import React, { useState, useEffect, useMemo } from "react";
import { useQuote } from "../context/QuoteContext";
import api from "../api";
import { formatEur, formatDateIt, findQuoteSnippet, quoteModeGroup, copyText } from "../utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronDown, Trash2, FolderOpen, Loader2, Search, User, X, Share2, Link2, Check, Globe, Copy, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const photoSrc = (u) => (/^(data:|https?:)/.test(u) ? u : `${BACKEND_URL}${u}`);
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";

const MODE_BADGE = {
  objects: "Oggetti", area: "Area", area_done: "Area",
  text: "Testo", text_done: "Testo", image: "Immagine", image_done: "Immagine",
};

const PAGE_SIZE = 5;

const Highlight = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const parts = String(text).split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
  return <>{parts.map((p, i) => p.toLowerCase() === query.toLowerCase()
    ? <mark key={i} className="bg-amber-200 text-slate-900 rounded px-0.5">{p}</mark>
    : <span key={i}>{p}</span>)}</>;
};

export default function QuotesHistory() {
  const { loadSavedQuote, user, duplicateQuote } = useQuote();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const [tab, setTab] = useState("saved"); // saved | with_me | by_me
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [modeFilter, setModeFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { data, isLoading } = useQuery({
    queryKey: ["quotes"],
    queryFn: async () => (await api.get("/quotes")).data.quotes,
  });

  const { data: trashData, isLoading: trashLoading } = useQuery({
    queryKey: ["quotes", "trash"],
    queryFn: async () => (await api.get("/quotes/trash")).data.quotes,
  });

  const del = async (id) => {
    await api.delete(`/quotes/${id}`);
    qc.invalidateQueries({ queryKey: ["quotes"] });
    toast.success("Preventivo spostato nel cestino", {
      action: {
        label: "Annulla",
        onClick: async () => {
          try { await api.post(`/quotes/${id}/restore`); qc.invalidateQueries({ queryKey: ["quotes"] }); toast.success("Preventivo ripristinato"); }
          catch { toast.error("Impossibile ripristinare"); }
        },
      },
    });
  };

  const restore = async (id) => {
    try { await api.post(`/quotes/${id}/restore`); toast.success("Preventivo ripristinato"); qc.invalidateQueries({ queryKey: ["quotes"] }); }
    catch { toast.error("Impossibile ripristinare"); }
  };

  const permanentDel = async (id) => {
    try { await api.delete(`/quotes/${id}/permanent`); toast.success("Preventivo eliminato definitivamente"); qc.invalidateQueries({ queryKey: ["quotes"] }); }
    catch { toast.error("Impossibile eliminare"); }
  };

  const toggleShare = async (q) => {
    const next = !q.shared;
    try {
      await api.patch(`/quotes/${q.id}/share`, { shared: next });
      toast.success(next ? "Preventivo condiviso con tutti gli utenti" : "Condivisione rimossa");
      qc.invalidateQueries({ queryKey: ["quotes"] });
    } catch {
      toast.error("Impossibile aggiornare la condivisione");
    }
  };

  const copyLink = async (q) => {
    const url = `${window.location.origin}/?quote=${q.id}`;
    const ok = await copyText(url);
    if (ok) {
      setCopiedId(q.id);
      toast.success("Link copiato negli appunti");
      setTimeout(() => setCopiedId((c) => (c === q.id ? null : c)), 2000);
    } else {
      toast.error("Impossibile copiare il link");
    }
  };

  const allQuotes = useMemo(() => data || [], [data]);
  const trashQuotes = useMemo(() => trashData || [], [trashData]);
  const quotes = tab === "trash" ? trashQuotes : allQuotes;
  const myId = user?.id;

  // counts per tab
  const counts = useMemo(() => {
    let saved = 0, withMe = 0, byMe = 0;
    allQuotes.forEach((q) => {
      const mine = q.user_id === myId;
      if (mine) saved += 1;
      if (q.shared && mine) byMe += 1;
      if (q.shared && !mine) withMe += 1;
    });
    return { saved, withMe, byMe, trash: trashQuotes.length };
  }, [allQuotes, trashQuotes, myId]);

  // reset pagination when filters/tab change
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [query, sortBy, modeFilter, tab]);

  const processed = useMemo(() => {
    let list = quotes.map((q) => ({ q, match: query.trim() ? findQuoteSnippet(q, query) : null }));
    // tab scope
    list = list.filter(({ q }) => {
      if (tab === "trash") return true;
      const mine = q.user_id === myId;
      if (tab === "saved") return mine;
      if (tab === "by_me") return q.shared && mine;
      if (tab === "with_me") return q.shared && !mine;
      return true;
    });
    if (modeFilter !== "all") list = list.filter(({ q }) => quoteModeGroup(q.mode) === modeFilter);
    if (query.trim()) list = list.filter(({ match }) => match);
    list.sort((a, b) => {
      if (sortBy === "price_desc") return (b.q.totale_mensile || 0) - (a.q.totale_mensile || 0);
      if (sortBy === "price_asc") return (a.q.totale_mensile || 0) - (b.q.totale_mensile || 0);
      if (tab === "trash") return new Date(b.q.deleted_at || b.q.created_at) - new Date(a.q.deleted_at || a.q.created_at);
      return new Date(b.q.created_at) - new Date(a.q.created_at);
    });
    return list;
  }, [quotes, query, sortBy, modeFilter, tab, myId]);

  const visible = processed.slice(0, visibleCount);

  const TABS = [
    { key: "saved", label: "Preventivi salvati", count: counts.saved },
    { key: "with_me", label: "Condivisi con me", count: counts.withMe },
    { key: "by_me", label: "Condivisi da me", count: counts.byMe },
    { key: "trash", label: "Cestino", count: counts.trash },
  ];

  return (
    <div className="mt-10">
      <h2 className="text-xl font-display font-semibold tracking-tight mb-4">Preventivi</h2>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="quotes-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`quotes-tab-${t.key}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === t.key ? "bg-[#1a5f5a] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {t.label} <span className={`ml-1 text-xs ${tab === t.key ? "text-white/70" : "text-slate-400"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca tra clienti, oggetti, note, testi, mercato…"
            className="pl-9 pr-9" data-testid="quotes-search-input" />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="quotes-search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-48" data-testid="quotes-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="date">Data (recenti)</SelectItem>
            <SelectItem value="price_desc">Prezzo (alto → basso)</SelectItem>
            <SelectItem value="price_asc">Prezzo (basso → alto)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={modeFilter} onValueChange={setModeFilter}>
          <SelectTrigger className="w-full sm:w-40" data-testid="quotes-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            <SelectItem value="objects">Oggetti</SelectItem>
            <SelectItem value="area">Area</SelectItem>
            <SelectItem value="text">Testo</SelectItem>
            <SelectItem value="image">Immagine</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(tab === "trash" ? trashLoading : isLoading) ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento...</div>
      ) : processed.length === 0 ? (
        <div className="text-slate-400 text-sm border border-dashed border-slate-300 rounded-lg p-8 text-center bg-slate-50" data-testid="quotes-empty">
          {tab === "trash"
            ? "Il cestino è vuoto."
            : (quotes.length === 0 ? "Nessun preventivo salvato ancora." : "Nessun preventivo corrisponde ai filtri.")}
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {visible.map(({ q, match }) => {
              let items = [];
              try { items = JSON.parse(q.items_json || "[]"); } catch {}
              const open = expanded === q.id;
              const canManage = q.user_id === myId || user?.role === "admin";
              let photos = [];
              try { photos = JSON.parse(q.photo_urls_json || "[]"); } catch { photos = []; }
              if (!photos.length && q.input_image_url) photos = [q.input_image_url];
              return (
                <div key={q.id} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden"
                  data-testid={`quote-card-${q.id}`}>
                  <div className="p-4 flex items-center justify-between gap-4">
                    <button className="flex-1 text-left min-w-0" onClick={() => setExpanded(open ? null : q.id)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-[#134744] bg-[#1a5f5a]/10 px-2 py-0.5 rounded-full">#{q.number ?? "—"}</span>
                        <span className="font-semibold text-slate-900">{q.cliente_nome || "Cliente non specificato"}</span>
                        <Badge variant="secondary" className="text-[10px]">{MODE_BADGE[q.mode] || "-"}</Badge>
                        {q.shared && (
                          <Badge className="text-[10px] bg-[#1a5f5a] hover:bg-[#1a5f5a] text-white gap-1" data-testid={`quote-shared-badge-${q.id}`}>
                            <Globe className="h-3 w-3" /> Condiviso
                          </Badge>
                        )}
                        {q.copied_from ? (
                          <Badge className="text-[10px] bg-amber-100 hover:bg-amber-100 text-amber-700 gap-1" data-testid={`quote-copied-badge-${q.id}`}>
                            <Copy className="h-3 w-3" /> Copia di #{q.copied_from}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{formatDateIt(q.created_at)}</span>
                        {q.created_by && <span className="flex items-center gap-1" data-testid={`quote-created-by-${q.id}`}><User className="h-3 w-3" /> {q.created_by}</span>}
                      </div>
                      <div className="text-sm mt-1">
                        <span className="font-semibold text-[#1a5f5a]">€{formatEur(q.totale_mensile)}/mese</span>
                        {q.totale_ritiro > 0 && <span className="text-slate-500"> · + €{formatEur(q.totale_ritiro)} ritiro</span>}
                      </div>
                      {match && (
                        <div className="mt-2 text-xs text-slate-500 bg-amber-50 border border-amber-100 rounded-md px-2.5 py-1.5" data-testid={`quote-snippet-${q.id}`}>
                          <span className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mr-1.5">{match.label}:</span>
                          <Highlight text={match.snippet} query={query.trim()} />
                        </div>
                      )}
                      {photos.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-2" data-testid={`quote-photos-${q.id}`}>
                          {photos.slice(0, 5).map((u, i) => (
                            <img key={i} src={photoSrc(u)} alt={`Foto ${i + 1}`} loading="lazy"
                              className="h-11 w-11 rounded-md object-cover border border-slate-200 bg-slate-50"
                              data-testid={`quote-photo-thumb-${q.id}-${i}`} />
                          ))}
                          {photos.length > 5 && (
                            <span className="h-11 w-11 rounded-md border border-slate-200 bg-slate-100 text-[11px] text-slate-500 flex items-center justify-center">
                              +{photos.length - 5}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      {tab === "trash" ? (
                        <>
                          {q.deleted_at && (
                            <span className="text-xs text-slate-400 mr-1 hidden sm:inline" data-testid={`quote-deleted-at-${q.id}`}>
                              eliminato il {formatDateIt(q.deleted_at)}
                            </span>
                          )}
                          {canManage && (
                            <Button size="sm" onClick={() => restore(q.id)} data-testid={`quote-restore-${q.id}`}
                              className="bg-[#1a5f5a] hover:bg-[#134744] text-white gap-1.5 h-9">
                              <RotateCcw className="h-4 w-4" /> Ripristina
                            </Button>
                          )}
                          {canManage && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50 h-9 w-9"
                                  data-testid={`quote-delete-permanent-${q.id}`} title="Elimina definitivamente">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Eliminare definitivamente?</AlertDialogTitle>
                                  <AlertDialogDescription>Questa azione è irreversibile: il preventivo verrà rimosso per sempre.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => permanentDel(q.id)} className="bg-red-500 hover:bg-red-600">Elimina per sempre</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </>
                      ) : (
                        <>
                          <Button size="sm" onClick={() => loadSavedQuote(q)} data-testid={`quote-open-${q.id}`}
                            className="bg-[#1a5f5a] hover:bg-[#134744] text-white gap-1.5 h-9">
                            <FolderOpen className="h-4 w-4" /> Apri
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => duplicateQuote(q)} title="Duplica con soglie attuali"
                            className="text-slate-500 hover:text-amber-700 hover:bg-amber-50 h-9 w-9" data-testid={`quote-duplicate-${q.id}`}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          {q.shared && (
                            <Button size="icon" variant="ghost" onClick={() => copyLink(q)} title="Copia link condiviso"
                              className="text-slate-500 hover:text-[#1a5f5a] hover:bg-[#1a5f5a]/10 h-9 w-9" data-testid={`quote-copy-link-${q.id}`}>
                              {copiedId === q.id ? <Check className="h-4 w-4 text-green-600" /> : <Link2 className="h-4 w-4" />}
                            </Button>
                          )}
                          {canManage && (
                            <Button size="icon" variant="ghost" onClick={() => toggleShare(q)}
                              title={q.shared ? "Rimuovi condivisione" : "Condividi con tutti gli utenti"}
                              className={`h-9 w-9 ${q.shared ? "text-[#1a5f5a] bg-[#1a5f5a]/10 hover:bg-[#1a5f5a]/20" : "text-slate-400 hover:text-[#1a5f5a] hover:bg-[#1a5f5a]/10"}`}
                              data-testid={`quote-share-toggle-${q.id}`}>
                              <Share2 className="h-4 w-4" />
                            </Button>
                          )}
                          {canManage && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50 h-9 w-9"
                                  data-testid={`quote-delete-${q.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Spostare nel cestino?</AlertDialogTitle>
                                  <AlertDialogDescription>Il preventivo verrà spostato nel cestino e potrai ripristinarlo in qualsiasi momento.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => del(q.id)} className="bg-red-500 hover:bg-red-600">Sposta nel cestino</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </>
                      )}
                      <button onClick={() => setExpanded(open ? null : q.id)} className="p-1 text-slate-400">
                        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                  </div>
                  {open && (
                    <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-sm">
                      {items.length > 0 ? (
                        <ul className="space-y-1">
                          {items.map((it, i) => (
                            <li key={i} className="flex justify-between text-slate-600">
                              <span>{it.qty}× {it.tipo}</span>
                              <span>€{formatEur((it.pricePerMonth || 0) * it.qty)}/mese</span>
                            </li>
                          ))}
                        </ul>
                      ) : <p className="text-slate-400">Nessun oggetto.</p>}
                      {q.cliente_cap && <p className="text-slate-500 mt-2">Ritiro: {q.cliente_citta} ({q.cliente_cap})</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {processed.length > visibleCount && (
            <div className="flex justify-center mt-5">
              <Button variant="outline" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)} className="gap-1.5" data-testid="quotes-show-more-button">
                <ChevronDown className="h-4 w-4" /> Mostra altri ({processed.length - visibleCount})
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
